/**
 * The per-tenant service catalog (`service_types`, API migration 0310).
 *
 * Typed to the ACTUAL payload of `GET /api/v1/service-types`, which is
 * enveloped and camelCase throughout:
 *
 *     { success: true, data: [{ id, companyId, name, isStandard, isActive,
 *                              displayOrder, triggersTorqueSheet,
 *                              createdAt, updatedAt }] }
 *
 * WHAT THIS CATALOG IS NOT
 * ------------------------
 * It is not `line_item_type`. `jobs.services_required` is jsonb holding an
 * array of DISPLAY LABEL STRINGS, and that is not changing — the catalog
 * supplies the labels offered today, not a key the job rows point at. A job
 * saved years ago can hold a label this catalog has never contained
 * ("Casing Running Tool (CRT)", "Torque Monitoring (Computers)",
 * "Vacuum Drifting"), and those must still render. See the union in
 * `NewJobScreen`'s Services Required section.
 */

/** One row of a tenant's service catalog. */
export interface ServiceType {
  id: string;
  companyId: string;
  name: string;
  /** Seeded by the platform. Renameable and deactivatable, but NOT deletable. */
  isStandard: boolean;
  isActive: boolean;
  displayOrder: number;
  /**
   * Whether a job carrying this service queues a torque sheet — the Needs PDF
   * tab on Dispatch. Settable on PATCH only (api #521); POST always creates a
   * custom row with it false.
   */
  triggersTorqueSheet: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fields a client may PATCH. The API reads these keys camelCase and answers
 * 400 on a non-boolean `triggersTorqueSheet`; standard rows accept it too.
 */
export interface ServiceTypePayload {
  name?: string;
  isActive?: boolean;
  displayOrder?: number;
  triggersTorqueSheet?: boolean;
}

/**
 * Normalize one row from the wire.
 *
 * The live payload is camelCase, but reads stay tolerant of snake_case per the
 * house rule — an `isActive` that arrives as `is_active` and reads `undefined`
 * would silently drop every service from the picker, which looks exactly like
 * "this tenant has no services" rather than like a bug.
 *
 * `isActive` defaults to true and `isStandard` to false: a row the server sent
 * us is a row that exists, and the safe failure is showing a service that
 * should have been hidden rather than hiding one that should have been shown.
 */
export function toServiceType(raw: any): ServiceType | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id;
  const name = raw.name;
  if (typeof id !== 'string' || typeof name !== 'string') return null;

  const displayOrder = Number(raw.displayOrder ?? raw.display_order);

  return {
    id,
    companyId: raw.companyId ?? raw.company_id ?? '',
    name,
    isStandard: (raw.isStandard ?? raw.is_standard) === true,
    isActive: (raw.isActive ?? raw.is_active) !== false,
    // Absent order sorts last rather than colliding with the real 0.
    displayOrder: Number.isFinite(displayOrder) ? displayOrder : UNSET_DISPLAY_ORDER,
    triggersTorqueSheet: (raw.triggersTorqueSheet ?? raw.triggers_torque_sheet) === true,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    updatedAt: raw.updatedAt ?? raw.updated_at ?? '',
  };
}

/**
 * Sort sentinel for a row that arrived without a `displayOrder`.
 *
 * It sorts such a row last instead of colliding with a real `0`. It is a local
 * sorting device and NOT a value the server ever sent — never PATCH it back.
 */
export const UNSET_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER;

/** Catalog order: `displayOrder`, then name for ties. */
export function compareServiceTypes(a: ServiceType, b: ServiceType): number {
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  return a.name.localeCompare(b.name);
}
